<template>
  <v-container fluid :class="embedded ? 'px-0' : 'mt-16 px-6'">
    <v-card flat>
      <v-card-title class="d-flex align-center flex-wrap">
        <h2 v-if="!embedded">Leads</h2>
        <v-chip
          small
          :class="embedded ? '' : 'ml-3'"
          color="blue-grey"
          dark
          v-if="isAdmin"
        >
          Admin view — all agents (read-only)
        </v-chip>
        <v-spacer></v-spacer>
        <add-lead-modal v-if="canAdd" class="mr-2" @saved="onLeadSaved" />
        <v-btn icon @click="refresh" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <!-- ===================== DASHBOARD ===================== -->
      <v-card-text class="pt-0">
        <!-- Conversion summary (reflects the active filters) -->
        <v-card outlined class="pa-3 mb-2 conversion-card">
          <div class="d-flex align-center mb-2">
            <v-icon small color="green darken-1" class="mr-1"
              >mdi-chart-donut</v-icon
            >
            <span class="text-caption grey--text text-uppercase font-weight-bold"
              >Conversion Summary</span
            >
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">filtered view</span>
          </div>
          <v-row dense align="center">
            <v-col cols="6" sm="3" md="2" class="text-center">
              <div class="text-h5 font-weight-bold">
                {{ conversionSummary.total }}
              </div>
              <div class="text-caption grey--text">Total leads</div>
            </v-col>
            <v-col cols="6" sm="3" md="2" class="text-center">
              <div class="text-h5 font-weight-bold green--text">
                {{ conversionSummary.converted }}
              </div>
              <div class="text-caption grey--text">Converted</div>
            </v-col>
            <v-col cols="6" sm="3" md="2" class="text-center">
              <div class="text-h5 font-weight-bold blue-grey--text">
                {{ conversionSummary.open }}
              </div>
              <div class="text-caption grey--text">Open</div>
            </v-col>
            <v-col cols="6" sm="3" md="2" class="text-center">
              <div
                class="text-h5 font-weight-bold"
                :class="
                  conversionSummary.followUpsDue
                    ? 'amber--text text--darken-3'
                    : 'grey--text'
                "
              >
                {{ conversionSummary.followUpsDue }}
              </div>
              <div class="text-caption grey--text">Follow-ups due</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex justify-space-between align-center">
                <span class="text-caption grey--text">Conversion rate</span>
                <span class="text-caption font-weight-bold"
                  >{{ conversionSummary.rate.toFixed(1) }}%</span
                >
              </div>
              <v-progress-linear
                :value="conversionSummary.rate"
                color="green"
                height="8"
                rounded
                class="my-1"
              ></v-progress-linear>
              <div class="text-right">
                <span class="text-subtitle-1 font-weight-bold"
                  >₱{{ compact(conversionSummary.sales) }}</span
                >
                <span class="text-caption grey--text"> sales</span>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <v-row dense>
          <v-col
            v-for="p in periodStats"
            :key="p.key"
            cols="12"
            sm="4"
          >
            <v-card outlined class="pa-3 period-card">
              <div class="d-flex align-center mb-1">
                <v-icon small :color="p.color" class="mr-1">{{ p.icon }}</v-icon>
                <span class="text-caption grey--text text-uppercase">{{
                  p.label
                }}</span>
              </div>
              <div class="d-flex justify-space-between align-end">
                <div>
                  <div class="text-h5 font-weight-bold">{{ p.leads }}</div>
                  <div class="text-caption grey--text">leads</div>
                </div>
                <div class="text-center">
                  <div class="text-h6 font-weight-bold green--text">
                    {{ p.converted }}
                  </div>
                  <div class="text-caption grey--text">converted</div>
                </div>
                <div class="text-right">
                  <div class="text-subtitle-1 font-weight-bold">
                    ₱{{ compact(p.amount) }}
                  </div>
                  <div class="text-caption grey--text">sales</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row dense>
          <!-- Grade mix -->
          <v-col cols="12" sm="4">
            <v-card outlined class="pa-3">
              <div class="text-caption grey--text text-uppercase mb-2">
                Grade mix
              </div>
              <div class="d-flex justify-space-around">
                <div
                  v-for="g in gradeCounts"
                  :key="g.grade"
                  class="text-center"
                >
                  <v-chip small :color="gradeColor(g.grade)" dark>{{
                    g.grade
                  }}</v-chip>
                  <div class="text-h6 font-weight-bold mt-1">{{ g.count }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <!-- Interest facets (click to filter) -->
          <v-col cols="12" sm="4">
            <v-card outlined class="pa-3 fill-height">
              <div class="d-flex align-center mb-2">
                <v-icon small color="indigo" class="mr-1">mdi-cart</v-icon>
                <span class="text-caption grey--text text-uppercase"
                  >Interest</span
                >
                <v-spacer></v-spacer>
                <v-btn
                  v-if="categoryFilter !== 'All'"
                  x-small
                  text
                  color="indigo"
                  @click="categoryFilter = 'All'"
                  >Clear</v-btn
                >
              </div>
              <div v-if="interestCounts.length" class="facet-scroll">
                <v-chip
                  v-for="i in interestCounts"
                  :key="i.name"
                  small
                  class="ma-1"
                  :color="categoryFilter === i.name ? 'indigo' : undefined"
                  :dark="categoryFilter === i.name"
                  :outlined="categoryFilter !== i.name"
                  @click="toggleCategory(i.name)"
                >
                  {{ i.name }}
                  <span class="ml-1 font-weight-bold">{{ i.count }}</span>
                </v-chip>
              </div>
              <div v-else class="grey--text">—</div>
            </v-card>
          </v-col>
          <!-- Source facets (click to filter) -->
          <v-col cols="12" sm="4">
            <v-card outlined class="pa-3 fill-height">
              <div class="d-flex align-center mb-2">
                <v-icon small color="deep-orange" class="mr-1"
                  >mdi-bullhorn</v-icon
                >
                <span class="text-caption grey--text text-uppercase"
                  >Source</span
                >
                <v-spacer></v-spacer>
                <v-btn
                  v-if="sourceFilter !== 'All'"
                  x-small
                  text
                  color="deep-orange"
                  @click="sourceFilter = 'All'"
                  >Clear</v-btn
                >
              </div>
              <div v-if="sourceCounts.length" class="facet-scroll">
                <v-chip
                  v-for="s in sourceCounts"
                  :key="s.name"
                  small
                  class="ma-1"
                  :color="sourceFilter === s.name ? 'deep-orange' : undefined"
                  :dark="sourceFilter === s.name"
                  :outlined="sourceFilter !== s.name"
                  @click="toggleSource(s.name)"
                >
                  {{ s.name }}
                  <span class="ml-1 font-weight-bold">{{ s.count }}</span>
                </v-chip>
              </div>
              <div v-else class="grey--text">—</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Team performance: follow-ups by person + conversions by agent -->
        <v-row dense>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-3 fill-height">
              <div class="text-caption grey--text text-uppercase mb-2">
                <v-icon small color="indigo" class="mr-1"
                  >mdi-phone-check</v-icon
                >Follow-ups by person
              </div>
              <div
                v-if="follow_up_leaderboard.length"
                class="leaderboard-list"
              >
                <div
                  v-for="u in follow_up_leaderboard"
                  :key="u.user_id"
                  class="d-flex justify-space-between align-center py-1"
                >
                  <span class="text-body-2">{{ u.name }}</span>
                  <v-chip x-small color="indigo" dark>{{ u.count }}</v-chip>
                </div>
              </div>
              <div v-else class="grey--text">No follow-ups logged yet.</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-3 fill-height">
              <div class="text-caption grey--text text-uppercase mb-2">
                <v-icon small color="green darken-1" class="mr-1"
                  >mdi-trophy</v-icon
                >Conversions by agent
              </div>
              <div v-if="conversionsByAgent.length" class="leaderboard-list">
                <div
                  v-for="a in conversionsByAgent"
                  :key="a.id"
                  class="d-flex justify-space-between align-center py-1"
                >
                  <span class="text-body-2">{{ a.name }}</span>
                  <div class="text-right text-no-wrap">
                    <v-chip x-small color="green" dark>{{
                      a.conversions
                    }}</v-chip>
                    <span class="text-caption grey--text ml-1"
                      >₱{{ compact(a.sales) }}</span
                    >
                  </div>
                </div>
              </div>
              <div v-else class="grey--text">No conversions yet.</div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Filters -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Search name / contact"
              clearable
              hide-details
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <v-select
              v-model="categoryFilter"
              :items="categoryFilterItems"
              label="Interested in"
              prepend-icon="mdi-cart"
              hide-details
              dense
            ></v-select>
          </v-col>
          <v-col cols="6" md="2">
            <v-select
              v-model="gradeFilter"
              :items="gradeFilterItems"
              label="Grade"
              prepend-icon="mdi-label"
              hide-details
              dense
            ></v-select>
          </v-col>
          <v-col cols="6" md="2">
            <v-select
              v-model="convertedFilter"
              :items="convertedFilterItems"
              item-text="label"
              item-value="value"
              label="Converted"
              prepend-icon="mdi-check-decagram"
              hide-details
              dense
            ></v-select>
          </v-col>
          <v-col cols="6" md="2" v-if="isAdmin">
            <v-select
              v-model="hatFilter"
              :items="hatFilterItems"
              item-text="label"
              item-value="value"
              label="Hat"
              prepend-icon="mdi-hat-fedora"
              hide-details
              dense
            >
              <template v-slot:append-outer>
                <v-menu offset-y left :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      title="Default hat for all accounts"
                    >
                      <v-icon small>mdi-cog</v-icon>
                    </v-btn>
                  </template>
                  <v-card width="280">
                    <v-card-text class="pb-1">
                      <div class="text-caption grey--text text-uppercase mb-1">
                        Default hat (all accounts)
                      </div>
                      <div class="text-body-2 mb-2">
                        Current default:
                        <strong>{{ defaultHatLabel }}</strong>
                      </div>
                      <div class="text-caption grey--text">
                        Applies this hat filter to the Leads page for every
                        account by default.
                      </div>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn
                        small
                        text
                        color="grey"
                        :disabled="!default_hat_id || savingDefaultHat"
                        @click="setDefaultHat(null)"
                      >
                        Clear
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn
                        small
                        depressed
                        color="primary"
                        :loading="savingDefaultHat"
                        :disabled="
                          hatFilter === 'all' || hatFilter === default_hat_id
                        "
                        @click="setDefaultHat(hatFilter)"
                      >
                        Set selected as default
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </template>
            </v-select>
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="dateFrom"
              type="date"
              label="From"
              hide-details
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="dateTo"
              type="date"
              label="To"
              hide-details
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <v-tabs v-model="activeTab">
        <v-tab><v-icon left small>mdi-account-multiple</v-icon>Active</v-tab>
        <v-tab
          ><v-icon left small>mdi-account-multiple-minus</v-icon>Deleted</v-tab
        >
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Active -->
        <v-tab-item>
          <v-data-table
            :headers="headers"
            :items="filteredLeads"
            :loading="loading"
            :items-per-page="15"
            dense
            class="mt-2 bordered-table rounded-lg"
          >
            <!-- Lead: name + source · branch + agent -->
            <template v-slot:item.lead="{ item }">
              <div class="py-1">
                <div class="font-weight-medium">{{ item.customer_name }}</div>
                <div class="text-caption grey--text">
                  <span v-if="item.lead_source">{{
                    item.lead_source.name
                  }}</span>
                  <span v-if="item.branch">
                    · {{ item.branch.branch_name }}</span
                  >
                  <span
                    v-if="item.contact_number && item.contact_number !== '0'"
                  >
                    · {{ item.contact_number }}</span
                  >
                </div>
                <div class="text-caption grey--text" v-if="item.agent">
                  <v-icon x-small class="mr-1">mdi-account-tie</v-icon
                  >{{ item.agent.full_name || item.agent.username }}
                </div>
              </div>
            </template>
            <template v-slot:item.category="{ item }">
              <v-chip x-small label v-if="item.category">{{
                item.category
              }}</v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.grade="{ item }">
              <v-chip
                x-small
                :color="gradeColor(item.grade)"
                dark
                v-if="item.grade"
                >{{ item.grade }}</v-chip
              >
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.converted="{ item }">
              <v-chip
                x-small
                :color="item.converted ? 'green' : 'blue-grey lighten-2'"
                dark
                >{{ item.converted ? "Converted" : "Open" }}</v-chip
              >
              <div
                v-if="item.converted && item.sale_amount"
                class="text-caption green--text font-weight-medium mt-1"
              >
                ₱{{ formatMoney(item.sale_amount) }}
              </div>
            </template>
            <!-- Follow-ups: count + next date; click to view/add -->
            <template v-slot:item.follow_ups="{ item }">
              <v-chip
                x-small
                :color="item.activities_count ? 'indigo' : 'grey lighten-2'"
                :dark="!!item.activities_count"
                @click="openFollowUps(item)"
                title="View / log follow-ups"
              >
                <v-icon x-small left>mdi-phone</v-icon
                >{{ item.activities_count || 0 }}
              </v-chip>
              <div
                v-if="item.next_follow_up_date"
                class="text-caption mt-1"
                :class="followUpDueClass(item)"
              >
                <v-icon x-small>mdi-calendar-clock</v-icon>
                {{ formatDate(item.next_follow_up_date) }}
              </div>
            </template>
            <template v-slot:item.created_at="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="text-no-wrap">{{
                    formatDate(item.created_at)
                  }}</span>
                </template>
                <div>
                  Inserted by:
                  {{
                    item.creator
                      ? item.creator.full_name || item.creator.username
                      : "—"
                  }}
                </div>
                <div>
                  Updated by:
                  {{
                    item.updater
                      ? item.updater.full_name || item.updater.username
                      : "—"
                  }}
                </div>
              </v-tooltip>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-end">
                <v-btn
                  icon
                  small
                  color="indigo"
                  v-if="canLogFollowUp"
                  @click="openFollowUps(item)"
                  title="Log follow-up"
                >
                  <v-icon small>mdi-phone-plus</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="openDetail(item)"
                  title="Details"
                >
                  <v-icon small>mdi-clipboard-text</v-icon>
                </v-btn>
                <v-btn
                  v-if="canEdit"
                  icon
                  small
                  color="warning"
                  @click="openEdit(item)"
                  title="Edit"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="canDelete"
                  icon
                  small
                  color="red"
                  @click="del(item)"
                  title="Delete"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- Deleted -->
        <v-tab-item>
          <v-data-table
            :headers="deletedHeaders"
            :items="deleted_leads"
            :loading="loadingDeleted"
            :items-per-page="15"
            class="mt-2"
          >
            <template v-slot:item.category="{ item }">
              <v-chip x-small label v-if="item.category">{{
                item.category
              }}</v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.deleted_at="{ item }">
              {{ formatDate(item.deleted_at) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="success"
                @click="restore(item)"
                title="Restore"
                v-if="canDelete"
              >
                <v-icon>mdi-restore</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>

      <p class="caption grey--text text-right pr-4 pb-2 mb-0">Leads.vue</p>
    </v-card>

    <edit-lead-modal
      :show="showEdit"
      :lead="selected"
      @close="showEdit = false"
      @saved="refresh"
    />
    <lead-detail-modal
      :show="showDetail"
      :lead-id="selectedId"
      :readonly="!canEdit"
      @close="showDetail = false"
      @changed="refresh"
    />

    <!-- Follow-ups dialog: timeline + quick log -->
    <v-dialog v-model="showFollowUps" max-width="520" scrollable>
      <v-card v-if="followUpLead">
        <v-toolbar flat dense color="primary" dark>
          <v-icon left>mdi-phone-log</v-icon>
          <v-toolbar-title class="text-truncate"
            >Follow-ups — {{ followUpLead.customer_name }}</v-toolbar-title
          >
          <v-spacer></v-spacer>
          <v-btn icon @click="showFollowUps = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-4">
          <template v-if="canLogFollowUp">
            <div class="section-label">Log a follow-up</div>
            <v-row dense>
              <v-col cols="7">
                <v-text-field
                  v-model="followUpForm.date"
                  type="date"
                  label="Date"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="5">
                <v-select
                  v-model="followUpForm.type"
                  :items="followUpTypes"
                  label="Type"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="followUpForm.notes"
                  label="Notes"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  hide-details
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="followUpForm.next_follow_up_date"
                  type="date"
                  label="Next follow-up (optional)"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
            <div class="text-right mt-2">
              <v-btn
                small
                color="primary"
                depressed
                :loading="savingFollowUp"
                :disabled="!followUpForm.date || !followUpForm.type"
                @click="submitFollowUp"
              >
                <v-icon left small>mdi-plus</v-icon>Add follow-up
              </v-btn>
            </div>
            <v-divider class="my-3"></v-divider>
          </template>

          <div class="section-label">History</div>
          <div v-if="loadingFollowUps" class="text-center py-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="24"
            ></v-progress-circular>
          </div>
          <v-timeline
            dense
            v-else-if="followUpActivities.length"
            class="pt-2"
          >
            <v-timeline-item
              v-for="a in followUpActivities"
              :key="a.id"
              small
              color="indigo"
            >
              <div class="d-flex justify-space-between align-center">
                <strong>{{ a.type }}</strong>
                <span class="text-caption grey--text">{{
                  formatDate(a.date)
                }}</span>
              </div>
              <div class="text-body-2" v-if="a.notes">{{ a.notes }}</div>
              <div class="text-caption grey--text" v-if="a.user">
                by {{ a.user.full_name || a.user.username }}
              </div>
              <div
                class="text-caption indigo--text"
                v-if="a.next_follow_up_date"
              >
                Next: {{ formatDate(a.next_follow_up_date) }}
              </div>
            </v-timeline-item>
          </v-timeline>
          <div v-else class="grey--text text-center py-3">
            No follow-ups logged yet.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AddLeadModal from "@/components/Lead/addLeadModal.vue";
import EditLeadModal from "@/components/Lead/editLeadModal.vue";
import LeadDetailModal from "@/components/Lead/leadDetailModal.vue";
import { LEAD_CATEGORIES, LEAD_GRADES } from "@/config/leadCategoryTemplates";

export default {
  components: { AddLeadModal, EditLeadModal, LeadDetailModal },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
      loadingDeleted: false,
      search: "",
      categoryFilter: "All",
      sourceFilter: "All",
      gradeFilter: "All",
      convertedFilter: "all",
      hatFilter: "all",
      savingDefaultHat: false,
      dateFrom: "",
      dateTo: "",
      categoryFilterItems: ["All", ...LEAD_CATEGORIES],
      gradeFilterItems: ["All", ...LEAD_GRADES],
      convertedFilterItems: [
        { label: "All", value: "all" },
        { label: "Converted", value: "yes" },
        { label: "Open", value: "no" },
      ],
      showEdit: false,
      showDetail: false,
      selected: null,
      selectedId: null,
      // ---- Follow-ups dialog ----
      showFollowUps: false,
      followUpLead: null,
      followUpActivities: [],
      loadingFollowUps: false,
      savingFollowUp: false,
      followUpForm: { date: "", type: "Call", notes: "", next_follow_up_date: "" },
      followUpTypes: ["Call", "Message", "Email", "Visit", "Other"],
      headers: [
        { text: "Lead", value: "lead", sortable: false },
        { text: "Interest", value: "category" },
        { text: "Grade", value: "grade" },
        { text: "Status", value: "converted" },
        { text: "Follow-ups", value: "follow_ups", sortable: false },
        { text: "Created", value: "created_at" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      deletedHeaders: [
        { text: "ID", value: "id" },
        { text: "Customer", value: "customer_name" },
        { text: "Interested In", value: "category" },
        { text: "Deleted At", value: "deleted_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      all_leads: "lead/all_leads",
      deleted_leads: "lead/deleted_leads",
      follow_up_leaderboard: "lead/follow_up_leaderboard",
      default_hat_id: "lead/default_hat_id",
      hats: "permission/hats",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    isAdmin() {
      return this.hasPermission("leads.view_all");
    },
    // Management actions are gated on the hat permission keys (leads.add /
    // leads.edit / leads.delete), NOT on access_level — so any role granted the
    // permission (e.g. Online Inquiry Officer, Sales Agent) can use it.
    canAdd() {
      return this.hasPermission("leads.add");
    },
    canEdit() {
      return this.hasPermission("leads.edit");
    },
    canDelete() {
      return this.hasPermission("leads.delete");
    },
    canManage() {
      return this.canAdd || this.canEdit || this.canDelete;
    },
    // Anyone who manages leads (or an admin viewer) may log follow-ups;
    // the server still enforces ownership/branch rules on top.
    canLogFollowUp() {
      return this.isAdmin || this.canEdit || this.canAdd || this.isSalesAgent;
    },
    // Conversion summary reflects the currently filtered leads.
    conversionSummary() {
      const list = this.filteredLeads;
      const total = list.length;
      const today = this.ymd(new Date());
      let converted = 0;
      let sales = 0;
      let followUpsDue = 0;
      list.forEach((l) => {
        if (l.converted) {
          converted++;
          sales += Number(l.sale_amount || 0);
        } else if (
          l.next_follow_up_date &&
          this.dateOnly(l.next_follow_up_date) <= today
        ) {
          followUpsDue++;
        }
      });
      return {
        total,
        converted,
        open: total - converted,
        rate: total ? (converted / total) * 100 : 0,
        sales,
        followUpsDue,
      };
    },
    // Conversions grouped by the owning agent (reflects the active filters).
    conversionsByAgent() {
      const map = {};
      this.filteredLeads.forEach((l) => {
        if (!l.converted || !l.agent) return;
        const id = l.agent.id;
        if (!map[id]) {
          map[id] = {
            id,
            name: l.agent.full_name || l.agent.username,
            conversions: 0,
            sales: 0,
          };
        }
        map[id].conversions++;
        map[id].sales += Number(l.sale_amount || 0);
      });
      return Object.values(map).sort((a, b) => b.conversions - a.conversions);
    },
    hatFilterItems() {
      return [
        { label: "All hats", value: "all" },
        ...this.hats.map((h) => ({ label: h.name, value: h.id })),
      ];
    },
    defaultHatLabel() {
      if (!this.default_hat_id) return "All hats";
      const h = this.hats.find((x) => x.id === this.default_hat_id);
      return h ? h.name : "Hat #" + this.default_hat_id;
    },
    filteredLeads() {
      let list = this.all_leads;
      if (this.categoryFilter !== "All") {
        list = list.filter((l) => l.category === this.categoryFilter);
      }
      if (this.sourceFilter !== "All") {
        list = list.filter(
          (l) => (l.lead_source ? l.lead_source.name : null) === this.sourceFilter
        );
      }
      if (this.gradeFilter !== "All") {
        list = list.filter((l) => l.grade === this.gradeFilter);
      }
      if (this.convertedFilter !== "all") {
        const want = this.convertedFilter === "yes";
        list = list.filter((l) => !!l.converted === want);
      }
      if (this.isAdmin && this.hatFilter !== "all") {
        list = list.filter(
          (l) =>
            l.agent &&
            Array.isArray(l.agent.hats) &&
            l.agent.hats.some((h) => h.id === this.hatFilter)
        );
      }
      if (this.dateFrom) {
        list = list.filter((l) => this.dateOnly(l.created_at) >= this.dateFrom);
      }
      if (this.dateTo) {
        list = list.filter((l) => this.dateOnly(l.created_at) <= this.dateTo);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter((l) =>
          [
            l.customer_name,
            l.contact_number,
            l.category,
            l.organization,
            l.branch ? l.branch.branch_name : null,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        );
      }
      return list;
    },
    // ---- Dashboard ----
    periodStats() {
      return [
        {
          key: "q",
          label: "This Quarter",
          icon: "mdi-calendar-range",
          color: "indigo",
          ...this.statsSince(this.startOfQuarter()),
        },
        {
          key: "m",
          label: "This Month",
          icon: "mdi-calendar-month",
          color: "blue",
          ...this.statsSince(this.startOfMonth()),
        },
        {
          key: "w",
          label: "This Week",
          icon: "mdi-calendar-week",
          color: "teal",
          ...this.statsSince(this.startOfWeek()),
        },
      ];
    },
    gradeCounts() {
      return LEAD_GRADES.map((grade) => ({
        grade,
        count: this.all_leads.filter((l) => l.grade === grade).length,
      }));
    },
    // All interests / sources, DESC by lead count (clickable facets).
    interestCounts() {
      return this.countsOf((l) => l.category);
    },
    sourceCounts() {
      return this.countsOf((l) => (l.lead_source ? l.lead_source.name : null));
    },
  },
  methods: {
    ...mapActions({
      get_all_leads: "lead/get_all_leads",
      get_deleted_leads: "lead/get_deleted_leads",
      delete_lead: "lead/delete_lead",
      restore_lead: "lead/restore_lead",
      get_sales_agents: "lead/get_sales_agents",
      get_lead_settings: "lead/get_lead_settings",
      update_lead_settings: "lead/update_lead_settings",
      get_lead: "lead/get_lead",
      add_activity: "lead/add_activity",
      get_follow_up_leaderboard: "lead/get_follow_up_leaderboard",
      fetch_hats: "permission/fetch_hats",
    }),
    followUpDueClass(item) {
      if (!item.next_follow_up_date) return "grey--text";
      const today = this.ymd(new Date());
      const d = this.dateOnly(item.next_follow_up_date);
      if (d < today) return "red--text font-weight-medium";
      if (d === today) return "orange--text text--darken-2 font-weight-medium";
      return "grey--text";
    },
    async openFollowUps(lead) {
      this.followUpLead = lead;
      this.showFollowUps = true;
      this.followUpActivities = [];
      this.followUpForm = {
        date: this.ymd(new Date()),
        type: "Call",
        notes: "",
        next_follow_up_date: "",
      };
      this.loadingFollowUps = true;
      try {
        const full = await this.get_lead(lead.id);
        this.followUpActivities = full.activities || [];
      } catch (e) {
        /* leave empty on failure */
      } finally {
        this.loadingFollowUps = false;
      }
    },
    async submitFollowUp() {
      if (!this.followUpLead) return;
      this.savingFollowUp = true;
      try {
        await this.add_activity({
          id: this.followUpLead.id,
          activity: {
            date: this.followUpForm.date,
            type: this.followUpForm.type,
            notes: this.followUpForm.notes || null,
            next_follow_up_date: this.followUpForm.next_follow_up_date || null,
          },
        });
        // Reload this lead's timeline + the list (updates count / next date).
        const full = await this.get_lead(this.followUpLead.id);
        this.followUpActivities = full.activities || [];
        this.followUpForm.notes = "";
        this.followUpForm.type = "Call";
        await this.get_all_leads();
        this.get_follow_up_leaderboard().catch(() => {});
        this.$alert({
          title: "Logged",
          message: "Follow-up recorded.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            e?.response?.data?.message || "Failed to log the follow-up.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.savingFollowUp = false;
      }
    },
    async setDefaultHat(hatId) {
      this.savingDefaultHat = true;
      try {
        await this.update_lead_settings({ default_hat_id: hatId || null });
        this.$alert({
          title: "Saved",
          message: hatId
            ? `Default hat set to "${this.defaultHatLabel}" for all accounts.`
            : "Default hat cleared for all accounts.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            e?.response?.data?.message || "Failed to save the default hat.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.savingDefaultHat = false;
      }
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    formatDate(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formatMoney(v) {
      return Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    compact(v) {
      v = Number(v || 0);
      if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
      if (v >= 1e3) return Math.round(v / 1e3) + "K";
      return v.toFixed(0);
    },
    gradeColor(g) {
      return { A: "green", B: "amber darken-2", C: "red" }[g] || "grey";
    },
    // ---- period helpers (YYYY-MM-DD, inclusive start → today) ----
    ymd(d) {
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${m}-${day}`;
    },
    startOfMonth() {
      const n = new Date();
      return this.ymd(new Date(n.getFullYear(), n.getMonth(), 1));
    },
    startOfQuarter() {
      const n = new Date();
      const q = Math.floor(n.getMonth() / 3) * 3;
      return this.ymd(new Date(n.getFullYear(), q, 1));
    },
    startOfWeek() {
      // ISO week — Monday as the first day.
      const n = new Date();
      const day = (n.getDay() + 6) % 7; // 0 = Monday
      const mon = new Date(n.getFullYear(), n.getMonth(), n.getDate() - day);
      return this.ymd(mon);
    },
    statsSince(start) {
      let leads = 0;
      let converted = 0;
      let amount = 0;
      this.all_leads.forEach((l) => {
        if (this.dateOnly(l.created_at) >= start) leads++;
        if (l.converted) {
          const cd = this.dateOnly(l.converted_at || l.created_at);
          if (cd >= start) {
            converted++;
            amount += Number(l.sale_amount || 0);
          }
        }
      });
      return { leads, converted, amount };
    },
    countsOf(fn) {
      const counts = {};
      this.all_leads.forEach((l) => {
        const k = fn(l);
        if (k) counts[k] = (counts[k] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    },
    toggleCategory(name) {
      this.categoryFilter = this.categoryFilter === name ? "All" : name;
    },
    toggleSource(name) {
      this.sourceFilter = this.sourceFilter === name ? "All" : name;
    },
    onLeadSaved() {
      // A newly added lead is owned by its creator; an active hat filter (e.g.
      // the global default like "Marketing Agent") would hide it if the creator
      // doesn't wear that hat. Reset to All hats so the new lead is visible.
      if (this.hatFilter !== "all") this.hatFilter = "all";
      this.refresh();
    },
    openDetail(lead) {
      this.selectedId = lead.id;
      this.showDetail = true;
    },
    openEdit(lead) {
      this.selected = lead;
      this.showEdit = true;
    },
    async del(lead) {
      const ok = await this.$confirm({
        title: "Delete Lead",
        message: `Delete lead for ${lead.customer_name}?`,
        color: "red",
        icon: "mdi-delete",
      });
      if (!ok) return;
      try {
        await this.delete_lead(lead.id);
        await this.fetchDeleted();
        this.$alert({
          title: "Deleted",
          message: "Lead moved to Deleted.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to delete lead.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async restore(lead) {
      try {
        await this.restore_lead(lead.id);
        await this.refresh();
        this.$alert({
          title: "Restored",
          message: "Lead restored.",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to restore lead.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async fetchDeleted() {
      this.loadingDeleted = true;
      try {
        await this.get_deleted_leads();
      } finally {
        this.loadingDeleted = false;
      }
    },
    async loadHats() {
      if (this.hats && this.hats.length) return;
      try {
        await this.fetch_hats();
      } catch (e) {
        /* non-fatal: hat filter just stays empty */
      }
    },
    async loadLeadSettings() {
      try {
        await this.get_lead_settings();
        // Seed the filter from the global default (only if the admin hasn't
        // already picked a hat this session).
        if (this.default_hat_id && this.hatFilter === "all") {
          this.hatFilter = this.default_hat_id;
        }
      } catch (e) {
        /* non-fatal: no default applied */
      }
    },
    async refresh() {
      this.loading = true;
      try {
        await this.get_all_leads();
      } finally {
        this.loading = false;
      }
      this.fetchDeleted();
      this.get_follow_up_leaderboard().catch(() => {});
    },
  },
  watch: {
    isAdmin(v) {
      if (v) {
        this.get_sales_agents();
        this.loadHats();
        this.loadLeadSettings();
      }
    },
  },
  created() {
    this.refresh();
    if (this.isAdmin) {
      this.get_sales_agents();
      this.loadHats();
      this.loadLeadSettings();
    }
  },
};
</script>

<style scoped>
.period-card {
  border-top: 3px solid #1976d2;
}
.conversion-card {
  border-left: 4px solid #43a047;
}
.leaderboard-list {
  max-height: 160px;
  overflow-y: auto;
}
.facet-scroll {
  max-height: 130px;
  overflow-y: auto;
}
</style>
